import { useState, FormEvent, useEffect } from 'react';
import {
  User,
  Bell,
  Shield,
  Loader2,
  Check,
  AlertCircle,
  Monitor,
  Smartphone,
  X,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import { getAuthCentreUrl } from '../utils/ssoAuth';

interface SettingsSectionProps {
  icon: React.ElementType;
  title: string;
  description: string;
  children: React.ReactNode;
}

function SettingsSection({ icon: Icon, title, description, children }: SettingsSectionProps) {
  return (
    <div className="card p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-10 h-10 rounded-lg bg-background-tertiary flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-foreground-secondary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-foreground-tertiary mt-0.5">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const authCentreUrl = getAuthCentreUrl();

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const [notificationPreferences, setNotificationPreferences] = useState({
    notifyProductUpdates: true,
    notifyUsageAlerts: true,
    notifyBilling: true,
    notifySecurity: true,
  });

  const [sessions, setSessions] = useState<Array<{
    id: string;
    userAgent?: string;
    ipAddress?: string;
    createdAt: string;
    expiresAt: string;
    isCurrent: boolean;
  }>>([]);
  const [showSessions, setShowSessions] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  useEffect(() => {
    loadNotificationPreferences();
  }, []);

  const loadNotificationPreferences = async () => {
    try {
      const response = await authApi.getNotificationPreferences();
      setNotificationPreferences(response.preferences);
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await authApi.getSessions();
      setSessions(response.sessions);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleProfileSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(false);
    setProfileLoading(true);

    try {
      await authApi.updateProfile({ firstName, lastName });
      await refreshUser();
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleNotificationToggle = async (key: keyof typeof notificationPreferences) => {
    const newValue = !notificationPreferences[key];
    setNotificationPreferences((prev) => ({ ...prev, [key]: newValue }));

    try {
      await authApi.updateNotificationPreferences({ [key]: newValue });
    } catch (err) {
      setNotificationPreferences((prev) => ({ ...prev, [key]: !newValue }));
      console.error('Failed to update notification preference:', err);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await authApi.revokeSession(sessionId);
      setSessions(sessions.filter((s) => s.id !== sessionId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke session');
    }
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        <p className="text-foreground-secondary mt-1">
          Manage your dashboard preferences
        </p>
      </div>

      <SettingsSection
        icon={User}
        title="Profile"
        description="Update your display name for this dashboard"
      >
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          {profileError && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {profileError}
            </div>
          )}

          {profileSuccess && (
            <div className="p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-success flex items-center gap-2">
              <Check className="w-4 h-4 flex-shrink-0" />
              Profile updated successfully
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="label">First name</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="input"
                placeholder="John"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="label">Last name</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="input"
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="input bg-background-tertiary/50 text-foreground-secondary cursor-not-allowed"
            />
            <p className="text-xs text-foreground-tertiary mt-1">
              Email and password are managed in{' '}
              <a href={authCentreUrl} className="text-accent hover:text-accent-hover">
                Tyaus Auth Centre
              </a>
            </p>
          </div>

          <div className="pt-2">
            <button type="submit" disabled={profileLoading} className="btn-primary">
              {profileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </button>
          </div>
        </form>
      </SettingsSection>

      <SettingsSection
        icon={Bell}
        title="Notifications"
        description="Configure how you receive notifications"
      >
        <div className="space-y-4">
          {[
            { key: 'notifyProductUpdates' as const, label: 'Product updates', description: 'News about new features and improvements' },
            { key: 'notifyUsageAlerts' as const, label: 'Usage alerts', description: 'Get notified when approaching usage limits' },
            { key: 'notifyBilling' as const, label: 'Billing notifications', description: 'Invoice and payment reminders' },
            { key: 'notifySecurity' as const, label: 'Security alerts', description: 'Important security notifications' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-foreground">{item.label}</p>
                <p className="text-xs text-foreground-tertiary">{item.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationPreferences[item.key]}
                  onChange={() => handleNotificationToggle(item.key)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-background-tertiary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent"></div>
              </label>
            </div>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Shield}
        title="Security"
        description="Account security and active sessions"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-foreground">Account management</p>
              <p className="text-xs text-foreground-tertiary">
                Change password, enable 2FA, or delete your account
              </p>
            </div>
            <a
              href={authCentreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
            >
              Auth Centre
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="divider" />

          <div className="py-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Active sessions</p>
                <p className="text-xs text-foreground-tertiary">
                  Dashboard login sessions on this platform
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSessions(!showSessions);
                  if (!showSessions) loadSessions();
                }}
                className="btn-secondary text-sm"
                disabled={sessionsLoading}
              >
                {sessionsLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : showSessions ? (
                  'Hide Sessions'
                ) : (
                  'View Sessions'
                )}
              </button>
            </div>

            {showSessions && (
              <div className="mt-4 space-y-2">
                {sessions.length === 0 ? (
                  <p className="text-xs text-foreground-tertiary">No active sessions</p>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-3 bg-background-secondary rounded-lg border border-border"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                          {session.userAgent?.toLowerCase().includes('mobile') ? (
                            <Smartphone className="w-4 h-4 text-foreground-secondary" />
                          ) : (
                            <Monitor className="w-4 h-4 text-foreground-secondary" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {session.userAgent || 'Unknown device'}
                            {session.isCurrent && (
                              <span className="ml-2 text-xs text-accent">(Current)</span>
                            )}
                          </p>
                          <p className="text-xs text-foreground-tertiary">
                            {session.ipAddress} • Created {new Date(session.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {!session.isCurrent && (
                        <button
                          onClick={() => handleRevokeSession(session.id)}
                          className="text-error hover:text-error/80 transition-colors"
                          title="Revoke session"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </SettingsSection>
    </div>
  );
}
